import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Loader2, Send, Trash2, AlertTriangle, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { fillWaTemplate, waCategoryLabel, waSaluto, insertAtCursor } from "../lib/waTemplates";
import WaPlaceholderChips from "./WaPlaceholderChips";
import { openWhatsApp, getLastWaStudioBlock } from "../lib/waLink";

const WaIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.516 5.26l-.999 3.648 3.472-.907zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
  </svg>
);

const toWaNumber = (raw) => {
  let d = (raw || "").replace(/[^\d]/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  // Numero IT senza prefisso internazionale: anteponi 39
  if (d && !d.startsWith("39") && (d.startsWith("3") || d.startsWith("0"))) {
    if (d.startsWith("0")) d = d.replace(/^0+/, "");
    d = "39" + d;
  }
  return d;
};

const fmtDateTime = (iso) => {
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso || ""; }
};

export default function WhatsAppDialog({ open, onOpenChange, praticaId, praticaNumero, client, assistitoKey = "principale", categoria = "cliente", defaultText, clienteNome, initialNumero }) {
  const [templates, setTemplates] = useState([]);
  const [numero, setNumero] = useState("");
  const [testo, setTesto] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [extra, setExtra] = useState({});
  const taRef = useRef(null);
  const base = `/studio/pratiche/${praticaId}/whatsapp-messages`;
  const nome = client?.ragione_sociale || "";
  const cellList = [client?.cell, ...(Array.isArray(client?.cellulari_extra) ? client.cellulari_extra : [])]
    .map((n, idx) => ({ idx, n: (n || "").trim() })).filter((x, i, a) => x.n && a.findIndex((y) => y.n === x.n) === i);
  const defaultCell = (cellList.find((x) => x.idx === (Number(client?.cell_principale) || 0)) || cellList[0])?.n || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(base, { params: { assistito: assistitoKey } });
      setMessages(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [base, assistitoKey]);

  useEffect(() => {
    if (open) {
      setNumero(initialNumero || defaultCell);
      const n = (client?.ragione_sociale || "").trim();
      const num = (praticaNumero || "").trim();
      const saluto = waSaluto();
      let msg = n ? `${saluto} ${n},\n` : "";
      if (num) msg += `in merito alla pratica n. ${num}, `;
      setTesto(defaultText != null ? defaultText : msg);
      load();
      if (praticaId) api.get(`/studio/pratiche/${praticaId}/wa-segnaposto`).then(({ data }) => setExtra(data || {})).catch(() => setExtra({}));
      api.get("/studio/me").then(({ data }) => { const t = (data?.settings?.wa_templates || {})[categoria]; setTemplates(Array.isArray(t) ? t.filter((s) => (s || "").trim()) : []); }).catch(() => setTemplates([]));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client, praticaNumero, load, categoria, defaultText]);

  const waNumber = toWaNumber(numero);

  const send = async () => {
    if (!waNumber) { toast.error("Numero destinatario non valido"); return; }
    if (!testo.trim()) { toast.error("Scrivi un messaggio"); return; }
    setSending(true);
    try {
      const finalText = await openWhatsApp(waNumber, fillWaTemplate(testo.trim(), { numero: praticaNumero, cliente: clienteNome || "", destinatario: nome, extra }));
      const hit = cellList.find((x) => toWaNumber(x.n) === waNumber);
      const etichetta = hit ? String((client?.cellulari_etichette || [])[hit.idx] || "").trim() : "";
      await api.post(base, { numero: numero.trim(), testo: finalText, firma_studio: getLastWaStudioBlock(), assistito_key: assistitoKey, assistito_nome: nome, etichetta });
      setTesto("");
      toast.success("Messaggio aperto in WhatsApp e registrato nella pratica");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Rimuovere questo messaggio dallo storico?")) return;
    try { await api.delete(`${base}/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const hasCell = cellList.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover text-popover-foreground border-border max-w-lg" data-testid="whatsapp-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2 text-base">
            <span className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-[#25D366]/15 text-[#25D366]"><WaIcon className="h-4 w-4" /></span>
            <span className="uppercase tracking-wide">WhatsApp · {nome || "Cliente"}</span>
            {categoria !== "cliente" && <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground" data-testid="whatsapp-categoria">{waCategoryLabel(categoria)}</span>}
          </DialogTitle>
        </DialogHeader>

        {!hasCell ? (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-3 text-sm" data-testid="whatsapp-no-cell">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <span className="text-foreground">{categoria === "cliente" ? "Questo cliente non ha un numero di cellulare in anagrafica. Aggiungilo per inviare un messaggio WhatsApp." : "Nessun numero di cellulare disponibile per questo soggetto."}</span>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Numero destinatario</label>
              {cellList.length > 1 ? (
                <Select value={numero} onValueChange={setNumero}>
                  <SelectTrigger className="mt-1 h-9 font-mono" data-testid="whatsapp-numero-select"><SelectValue placeholder="Seleziona numero" /></SelectTrigger>
                  <SelectContent>
                    {cellList.map((x) => (
                      <SelectItem key={x.idx} value={x.n} data-testid={`whatsapp-numero-option-${x.idx}`} className="font-mono">
                        {x.n}{(client?.cellulari_etichette || [])[x.idx] ? `  · ${client.cellulari_etichette[x.idx]}` : ""}{x.idx === (Number(client?.cell_principale) || 0) ? "  · principale" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
              <Input value={numero} onChange={(e) => setNumero(e.target.value)} data-testid="whatsapp-numero"
                className="mt-1 h-9 font-mono" placeholder="Es. 351 3390015" />
              )}
              {waNumber && <p className="mt-1 text-[11px] text-muted-foreground">Verrà usato: +{waNumber}</p>}
            </div>
            {templates.length > 0 && (
              <div data-testid="whatsapp-templates">
                <label className="text-xs font-medium text-muted-foreground">Messaggi preimpostati</label>
                <div className="mt-1 space-y-1 max-h-36 overflow-y-auto">
                  {templates.map((t, i) => (
                    <button key={i} type="button" data-testid={`whatsapp-template-${i}`}
                      onClick={() => setTesto(fillWaTemplate(t, { numero: praticaNumero, cliente: clienteNome || "", destinatario: nome, extra }))}
                      className="w-full text-left text-xs rounded-md border border-border bg-muted/40 hover:border-[#25D366] hover:bg-[#25D366]/10 px-2.5 py-1.5 whitespace-pre-wrap line-clamp-2 transition-colors">{fillWaTemplate(t, { numero: praticaNumero, cliente: clienteNome || "", destinatario: nome, extra })}</button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground">Messaggio</label>
              <div className="mt-1 mb-1.5"><WaPlaceholderChips onInsert={(ph) => {
                const v = fillWaTemplate(ph, { numero: praticaNumero, cliente: clienteNome || "", destinatario: nome, extra });
                if (!v.trim()) { toast.warning(`${ph}: dato non presente nella pratica`); return; }
                setTesto((t) => {
                  const s = taRef.current && typeof taRef.current.selectionStart === "number" ? taRef.current.selectionStart : t.length;
                  return insertAtCursor(taRef.current, t, (s > 0 && /\S/.test(t[s - 1]) ? " " : "") + v);
                });
              }} testId="whatsapp-ph" /></div>
              <Textarea ref={taRef} value={testo} onChange={(e) => setTesto(e.target.value)} data-testid="whatsapp-testo"
                rows={4} className="mt-1 resize-none" placeholder="Scrivi il messaggio da inviare…" />
            </div>
            {testo.trim() && (
              <div data-testid="whatsapp-preview">
                <label className="text-xs font-medium text-muted-foreground">Anteprima</label>
                <div className="mt-1 rounded-lg rounded-tr-none border border-[#25D366]/40 bg-[#25D366]/10 px-3 py-2 text-sm text-foreground whitespace-pre-wrap break-words max-h-40 overflow-y-auto" data-testid="whatsapp-preview-text">
                  {fillWaTemplate(testo.trim(), { numero: praticaNumero, cliente: clienteNome || "", destinatario: nome, extra })}
                </div>
              </div>
            )}
            <Button onClick={send} disabled={sending || !testo.trim()} data-testid="whatsapp-send"
              className="w-full h-10 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Invia via WhatsApp
            </Button>
          </div>
        )}

        <div className="pt-2 border-t border-border">
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-foreground">
            <MessageSquare className="h-4 w-4 text-[#25D366]" /> Messaggi WhatsApp
            <span className="text-xs font-normal text-muted-foreground" data-testid="whatsapp-count">({messages.length})</span>
          </div>
          <div className="max-h-56 overflow-y-auto space-y-2" data-testid="whatsapp-list">
            {loading ? (
              <div className="py-4 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#25D366]" /></div>
            ) : messages.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nessun messaggio inviato a questo {categoria === "cliente" ? "cliente" : "soggetto"}.</p>
            ) : messages.map((m) => (
              <div key={m.id} data-testid={`whatsapp-msg-${m.id}`} className="group rounded-md border border-border bg-muted/40 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-mono text-muted-foreground" data-testid={`whatsapp-msg-date-${m.id}`}>{fmtDateTime(m.sent_at)} · {m.numero}{m.etichetta && <span className="ml-1.5 inline-block rounded bg-emerald-100 px-1.5 py-px text-[10px] font-sans font-medium text-emerald-800" data-testid={`whatsapp-msg-label-${m.id}`}>{m.etichetta}</span>}</span>
                  <button onClick={() => del(m.id)} data-testid={`whatsapp-msg-del-${m.id}`} title="Rimuovi dallo storico"
                    className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-400 transition-opacity"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <p className="text-sm text-foreground whitespace-pre-wrap break-words mt-0.5">{m.testo || "—"}</p>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
